





























































































import { AxiosError } from 'axios';
import { PropType } from 'vue';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';

import auth from '@/mixins/auth';
import colorable from '@/mixins/colorable';
import mixins from '@/utils/mixins';
import { isHtmlTextEmpty } from '@/utils/helpers';
import { ErrorManager } from '@/models/error';
import {
  IEmployeeFormSubmissionComment,
  IEmployeeFormSubmission,
  IEmployeeFormSubmissionCommentData,
  EmployeeFormSubmissionComment,
  IUser,
  IAttachment,
  EmployeeFormSubmissionCommentAttachment
} from '@/models';

import EmployeeFormSubmissionCommentListItem from './EmployeeFormSubmissionCommentListItem.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import QuillEditor from '../common/QuillEditor.vue';
import CommentAttachmentList from '@/components/common/CommentAttachmentList.vue';

export default mixins(auth, colorable).extend({
  name: 'EmployeeFormSubmissionCommentCard',

  components: {
    CommentAttachmentList,
    ErrorAlert,
    EmployeeFormSubmissionCommentListItem,
    QuillEditor
  },

  props: {
    employeeFormSubmission: {
      type: Object as PropType<IEmployeeFormSubmission>,
      required: true
    },
    loading: {
      type: Boolean,
      default: () => false
    }
  },

  data: () => ({
    errorMessage: '',
    detailErrorMessage: '',

    commentValid: '',
    comment: {} as IEmployeeFormSubmissionCommentData,
    commentLoading: false,
    commentErrorMessage: '',
    commentDetailErrorMessage: '',
    mentionsLoading: false,

    attachments: [] as IAttachment[]
  }),

  computed: {
    comments: get<IEmployeeFormSubmissionComment[]>(
      'employeeForm/getEmployeeFormSubmissionComments'
    ),

    mentionableUsers: get<IUser[]>(
      'employeeForm/getEmployeeFormSubmissionMentionableUsers'
    ),

    getLastCreatedCommentId: get<null | number>(
      'employeeForm/getLastCreatedCommentId'
    ),

    internalLoading: {
      get(): boolean {
        return this.loading;
      },
      set(value: boolean): void {
        this.$emit('update:loading', value);
      }
    },

    canSaveComment(): boolean {
      return !isHtmlTextEmpty(this.comment.text) && !this.commentLoading;
    }
  },

  methods: {
    ...mapActions({
      fetchEmployeeFormSubmissionComments:
        'employeeForm/fetchEmployeeFormSubmissionComments',
      createEmployeeFormSubmissionComment:
        'employeeForm/createEmployeeFormSubmissionComment',
      fetchEmployeeFormSubmissionCommentsMentionableUsers:
        'employeeForm/fetchEmployeeFormSubmissionCommentsMentionableUsers',
      addEmployeeFormSubmissionCommentAttachments:
        'employeeForm/addEmployeeFormSubmissionCommentAttachments'
    }),

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchEmployeeFormSubmissionCommentsMentionableUsers(
          this.employeeFormSubmission.id
        );
      } catch (error) {
        this.commentErrorMessage = ErrorManager.extractApiError(
          error as AxiosError
        );
      } finally {
        this.mentionsLoading = false;
      }
    },

    clearAllErrors(): void {
      this.errorMessage = '';
      this.detailErrorMessage = '';

      this.commentErrorMessage = '';
      this.commentDetailErrorMessage = '';
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new EmployeeFormSubmissionCommentAttachment({ file: file })
        )
      );
    },

    addComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = EmployeeFormSubmissionComment.parseHtmlToMentionText(
        this.comment.text!
      );

      return this.createEmployeeFormSubmissionComment({
        submissionId: this.employeeFormSubmission.id,
        comment: { ...this.comment, text: parsedCommentMentionText }
      })
        .then(async () => {
          this.comment = {} as IEmployeeFormSubmissionCommentData;
          if (this.attachments.length !== 0) {
            await this.addEmployeeFormSubmissionCommentAttachments({
              submissionId: this.employeeFormSubmission.id,
              commentId: this.getLastCreatedCommentId,
              attachments: this.attachments
            });
            this.attachments = [];
          }
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.commentDetailErrorMessage = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    }
  },

  async mounted() {
    this.internalLoading = true;
    try {
      await this.fetchEmployeeFormSubmissionComments(
        this.employeeFormSubmission.id
      );
    } catch (err) {
      const error = err as AxiosError;
      if (error.response) {
        if (error.response.data.detail) {
          this.detailErrorMessage = error.response.data.detail;
        }
      }
      this.errorMessage = ErrorManager.extractApiError(error);
    } finally {
      this.internalLoading = false;
    }
  }
});
